import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { BaseModalComponent } from "@ha/ui/common";
import { HaTranslatePipe } from "@ha/util/pipes";
import { TranslocoService } from "@ngneat/transloco";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { EMPTY } from "rxjs";
import { catchError } from "rxjs/operators";

import {
    FlammableAtexPermitQuestionnaireFlat,
    FlammableAtexPermitQuestionnaireMeasurement,
    FlammableAtexPermitQuestionnaireQuestion,
} from "../../models/flammable-atex-permit-questionnaire.model";
import { FlammableAtexPermitQuestionnaireService } from "../../services/flammable-atex-permit-questionnaire.service";

export interface PermitGasFreeDeclarationComponentData {
    permitId: number;
}

@UntilDestroy()
@Component({
    templateUrl: "./permit-gas-free-declaration.component.html",
    styleUrls: ["./permit-gas-free-declaration.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [HaTranslatePipe],
})
export class PermitGasFreeDeclarationComponent extends BaseModalComponent implements OnInit {
    public data: PermitGasFreeDeclarationComponentData;

    public questionnaire: FlammableAtexPermitQuestionnaireFlat | null = null;

    public questions: FlammableAtexPermitQuestionnaireQuestion[] = [];

    public measurements: FlammableAtexPermitQuestionnaireMeasurement[] = [];

    public loading = true;

    public hasError = false;

    constructor(
        private flammableAtexPermitQuestionnaireService: FlammableAtexPermitQuestionnaireService,
        private translocoService: TranslocoService,
        private translatePipe: HaTranslatePipe,
        private cdr: ChangeDetectorRef,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.flammableAtexPermitQuestionnaireService.getQuestionnaireFlat(this.data.permitId).pipe(
            catchError(() => {
                this.loading = false;
                this.hasError = true;
                this.questionnaire = null;
                this.questions = [];
                this.measurements = [];
                this.cdr.markForCheck();
                return EMPTY;
            }),
            untilDestroyed(this),
        ).subscribe((questionnaire) => {
            const gasFreeSection = questionnaire.sections.find(
                (section) => section.translationKey === "gasFreeDeclaration",
            );

            this.questionnaire = questionnaire;
            this.questions = gasFreeSection?.questions ?? [];
            this.measurements = questionnaire.flammableMaterialMeasurements ?? [];

            this.loading = false;
            this.hasError = false;
            this.cdr.markForCheck();
        });
    }

    public get isEmpty(): boolean {
        return !this.loading && !this.hasError && this.questions.length === 0;
    }

    public getQuestionTranslations(question: FlammableAtexPermitQuestionnaireQuestion): string {
        return this.translatePipe.transform(question.translations ?? []);
    }

    public getAnswerValue(question: FlammableAtexPermitQuestionnaireQuestion): number {
        if (question.answer === 1) {
            return 1;
        }

        if (question.answer === 2) {
            return 2;
        }

        if (this.hasAnswer(question)) {
            return 1;
        }

        return 4;
    }

    public getConditionText(question: FlammableAtexPermitQuestionnaireQuestion): string | null {
        return question.conditionText ?? null;
    }

    public shouldShowConditionLabel(question: FlammableAtexPermitQuestionnaireQuestion): boolean {
        return question.showConditionOnYes !== true;
    }

    public getTextAnswer(question: FlammableAtexPermitQuestionnaireQuestion): string | null {
        return question.textAnswer ?? null;
    }

    public getNumberAnswer(question: FlammableAtexPermitQuestionnaireQuestion): number | null {
        return question.numberAnswer ?? null;
    }

    public getDateTimeAnswer(question: FlammableAtexPermitQuestionnaireQuestion): Date | null {
        return question.dateTimeAnswer ?? null;
    }

    public formatDateTime(value: Date): string {
        const locale = this.translocoService.getActiveLang() === "en" ? "en-US" : "sv-SE";

        return new Intl.DateTimeFormat(locale, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        }).format(new Date(value));
    }

    public hasAnswer(question: FlammableAtexPermitQuestionnaireQuestion): boolean {
        return question.answer != null
            || !!question.conditionText
            || !!question.textAnswer
            || question.numberAnswer != null
            || question.dateTimeAnswer != null;
    }
}
